import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Loader2, RefreshCw, Save, Search, Tag, LayoutGrid, Upload } from "lucide-react";

const API = `${API_BASE}/api`;

const CATEGORIES = [
  { value: "intro", label: "Intro / Recommendation" },
  { value: "seo_aeo", label: "SEO / AEO" },
  { value: "prospectid", label: "ProspectID" },
  { value: "prospectmax", label: "ProspectMax" },
  { value: "website", label: "Website" },
  { value: "pricing", label: "Pricing" },
  { value: "closing", label: "Closing" },
  { value: "other", label: "Other" },
];

const PRODUCT_TAGS = [
  "seo_aeo_one_time", "seo_aeo_ongoing",
  "prospectid_marketing",
  "prospectid_plus_prospectmax_existing_site",
  "prospectid_plus_website_and_prospectmax",
  "prospectmax_standalone",
  "website_design_plus_leadmgmt",
];

export default function SlideLibraryAdminPage() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [editing, setEditing] = useState(null); // slide doc being edited
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null, category: "other", product_tags: [], active: true,
  });
  const [uploading, setUploading] = useState(false);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/proposal-slide-library`, { params: { active_only: false } });
      setSlides(r.data || []);
    } catch (e) {
      toast.error("Failed to load slide library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlides(); }, []);

  const runBootstrap = async () => {
    setBootstrapping(true);
    try {
      const r = await axios.post(`${API}/proposal-slide-library/bootstrap`);
      const s = r.data || {};
      toast.success(`Indexed ${s.total_slides} slides (added ${s.added}, updated ${s.updated})`);
      fetchSlides();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bootstrap failed");
    } finally {
      setBootstrapping(false);
    }
  };

  // Admin-uploaded PPTX → every slide indexed as a library row. Category +
  // product tags apply to every slide in the deck; admin fine-tunes per-slide
  // afterward via the Edit dialog.
  const submitUpload = async () => {
    if (!uploadForm.file) {
      toast.error("Pick a .pptx file first");
      return;
    }
    if (!uploadForm.file.name.toLowerCase().endsWith(".pptx")) {
      toast.error("Only .pptx files are supported");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      const params = new URLSearchParams();
      if (uploadForm.category) params.append("category", uploadForm.category);
      if (uploadForm.product_tags.length) params.append("product_tags", uploadForm.product_tags.join(","));
      params.append("active", uploadForm.active ? "true" : "false");
      const r = await axios.post(
        `${API}/proposal-slide-library/upload?${params.toString()}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Added ${r.data.added} slide${r.data.added === 1 ? "" : "s"}`);
      setUploadOpen(false);
      setUploadForm({ file: null, category: "other", product_tags: [], active: true });
      fetchSlides();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return slides.filter((s) => {
      if (categoryFilter !== "all" && s.category !== categoryFilter) return false;
      if (tagFilter !== "all" && !(s.product_tags || []).includes(tagFilter)) return false;
      if (!term) return true;
      return (s.title || "").toLowerCase().includes(term)
          || (s.description || "").toLowerCase().includes(term)
          || (s.product_tags || []).some((t) => t.toLowerCase().includes(term));
    });
  }, [slides, search, categoryFilter, tagFilter]);

  const saveEdit = async () => {
    const s = editing;
    try {
      await axios.patch(`${API}/proposal-slide-library/${s.id}`, {
        title: s.title, description: s.description,
        product_tags: s.product_tags, category: s.category,
        order_hint: Number(s.order_hint) || 0, active: !!s.active,
      });
      toast.success("Slide updated");
      setEditing(null);
      fetchSlides();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (s) => {
    try {
      await axios.patch(`${API}/proposal-slide-library/${s.id}`, { active: !s.active });
      setSlides((prev) => prev.map((x) => x.id === s.id ? { ...x, active: !s.active } : x));
    } catch (e) {
      toast.error("Failed to toggle");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="slide-library-admin-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <LayoutGrid className="h-7 w-7 text-fuchsia-600" />
            Product Catalog Slides
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Master slides from <code className="bg-slate-100 rounded px-1">proposal_catalog.pptx</code> that the
            AI-driven Prospect IQ Proposal builder stitches into every generated deck. Retag or reorder here — no
            code deploy needed.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setUploadOpen(true)} data-testid="slide-library-upload-btn"
                  className="bg-fuchsia-600 hover:bg-fuchsia-700">
            <Upload className="h-4 w-4 mr-1" /> Upload PPTX
          </Button>
          <Button onClick={runBootstrap} disabled={bootstrapping} data-testid="slide-library-bootstrap-btn"
                  variant="outline">
            {bootstrapping ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Re-index from master deck
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative min-w-[240px]">
          <Search className="h-4 w-4 absolute left-2 top-3 text-slate-400" />
          <Input
            data-testid="slide-library-search"
            placeholder="Search title, description, or tag…"
            className="pl-8"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Category</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[220px]" data-testid="slide-library-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Product tag</Label>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-[280px]" data-testid="slide-library-tag-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {PRODUCT_TAGS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm text-slate-500 ml-auto self-center">
          {filtered.length} / {slides.length} slides
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-500">
          <Loader2 className="h-8 w-8 mx-auto animate-spin mb-2" />
          Loading slide library…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500 border rounded-lg bg-slate-50">
          No slides match. Try re-indexing from the master deck.
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
             data-testid="slide-library-grid">
          {filtered.map((s) => (
            <Card key={s.id} className={`overflow-hidden ${!s.active ? "opacity-60" : ""}`}
                  data-testid={`slide-card-${s.id}`}>
              <div className="relative bg-slate-100 aspect-video overflow-hidden border-b">
                {s.has_thumbnail ? (
                  <img
                    src={`${API}/proposal-slide-library/${s.id}/thumbnail`}
                    alt={s.title}
                    className="w-full h-full object-contain bg-white"
                    data-testid={`slide-thumb-${s.id}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">
                    No thumbnail
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                  <Badge variant="secondary" className="bg-white/90 text-slate-700 text-[10px] font-mono">
                    #{s.source_slide_idx}
                  </Badge>
                  <Badge variant="secondary" className="bg-white/90 text-fuchsia-700 text-[10px]">
                    {s.category}
                  </Badge>
                </div>
                <div className="absolute top-2 right-2">
                  <Switch
                    checked={!!s.active}
                    onCheckedChange={() => toggleActive(s)}
                    data-testid={`slide-toggle-active-${s.id}`}
                  />
                </div>
              </div>
              <CardContent className="p-3 space-y-2">
                <div className="font-semibold text-sm text-slate-900 line-clamp-2 min-h-[2.5em]">
                  {s.title || `Slide ${s.source_slide_idx}`}
                </div>
                {s.description && (
                  <div className="text-xs text-slate-500 line-clamp-2">{s.description}</div>
                )}
                <div className="flex flex-wrap gap-1">
                  {(s.product_tags || []).length === 0 ? (
                    <span className="text-[10px] text-slate-400 italic">No product tags</span>
                  ) : (
                    (s.product_tags || []).map(t => (
                      <Badge key={t} variant="outline" className="text-[10px]">
                        <Tag className="h-2.5 w-2.5 mr-0.5" />{t}
                      </Badge>
                    ))
                  )}
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-[10px] text-slate-400">Order hint: {s.order_hint ?? "—"}</span>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setEditing({ ...s, product_tags: s.product_tags || [] })}
                    data-testid={`slide-edit-btn-${s.id}`}
                  >
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="slide-library-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit slide metadata</DialogTitle>
            <DialogDescription>
              Update tags, category, description, and ordering. Changes take effect on the next generated proposal.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {editing.has_thumbnail && (
                <img
                  src={`${API}/proposal-slide-library/${editing.id}/thumbnail`}
                  alt={editing.title}
                  className="w-full max-h-64 object-contain bg-slate-100 rounded border"
                />
              )}
              <div>
                <Label>Title</Label>
                <Input
                  data-testid="edit-title-input"
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description (internal notes)</Label>
                <Textarea
                  data-testid="edit-description-input"
                  rows={2}
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category || "other"}
                          onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Order hint</Label>
                  <Input
                    data-testid="edit-order-input"
                    type="number"
                    value={editing.order_hint ?? 0}
                    onChange={(e) => setEditing({ ...editing, order_hint: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Product tags (appears in proposals for these bundles)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 p-3 border rounded-lg bg-slate-50 max-h-48 overflow-y-auto">
                  {PRODUCT_TAGS.map((t) => (
                    <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={`edit-tag-${t}`}
                        checked={(editing.product_tags || []).includes(t)}
                        onCheckedChange={(ck) => {
                          const cur = new Set(editing.product_tags || []);
                          if (ck) cur.add(t); else cur.delete(t);
                          setEditing({ ...editing, product_tags: Array.from(cur) });
                        }}
                      />
                      <code className="text-xs">{t}</code>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={!!editing.active}
                  onCheckedChange={(ck) => setEditing({ ...editing, active: ck })}
                  data-testid="edit-active-switch"
                />
                <span className="text-sm">Active — include in generated proposals</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="edit-save-btn"
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
              <Save className="h-4 w-4 mr-1" /> Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog — accepts a .pptx and indexes every slide with the
          chosen category + product tags applied to all slides. Admin can
          still fine-tune per-slide via the Edit dialog after upload. */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploading) setUploadOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="slide-library-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload a PPTX to the Slide Library</DialogTitle>
            <DialogDescription>
              Every slide in the file becomes a library row. Pick the category and product tags
              that apply to the whole file — you can fine-tune per-slide afterward.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>PowerPoint file (.pptx)</Label>
              <Input
                type="file"
                accept=".pptx"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                data-testid="upload-pptx-file"
                className="mt-1"
              />
              {uploadForm.file && (
                <p className="text-xs text-muted-foreground mt-1">
                  {uploadForm.file.name} · {(uploadForm.file.size / 1024).toFixed(0)} KB
                </p>
              )}
            </div>
            <div>
              <Label>Category</Label>
              <Select value={uploadForm.category}
                      onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}>
                <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Product tags (which bundles should include these slides?)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 p-3 border rounded-lg bg-slate-50 max-h-48 overflow-y-auto">
                {PRODUCT_TAGS.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      data-testid={`upload-tag-${t}`}
                      checked={uploadForm.product_tags.includes(t)}
                      onCheckedChange={(ck) => {
                        const cur = new Set(uploadForm.product_tags);
                        if (ck) cur.add(t); else cur.delete(t);
                        setUploadForm({ ...uploadForm, product_tags: Array.from(cur) });
                      }}
                    />
                    <code className="text-xs">{t}</code>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={uploadForm.active}
                onCheckedChange={(ck) => setUploadForm({ ...uploadForm, active: ck })}
                data-testid="upload-active-switch"
              />
              <span className="text-sm">Active — include in generated proposals</span>
            </label>
            <p className="text-xs text-muted-foreground border-l-2 border-amber-300 bg-amber-50 px-2 py-1.5 rounded">
              Note: Thumbnails need LibreOffice, which only exists on preview. If you upload here
              on preview and then re-publish, thumbnails ship with the deploy. On production alone,
              slides upload fine but show &ldquo;No preview&rdquo; until the next preview-side re-index.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={submitUpload} disabled={uploading || !uploadForm.file}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                    data-testid="upload-submit-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</>
                         : <><Upload className="h-4 w-4 mr-1" /> Upload &amp; index</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
